import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { EQUIPMENT_CATEGORIES, type EquipmentCategory } from '@mads/shared'
import { ErrorNote, Loading } from '@/components/Feedback'
import { Select } from '@/components/Select'
import { PatTag, WorkingTag } from '@/components/Tags'
import {
  parseInventorySearch,
  toInventoryFilters,
  type InventorySearch,
} from '@/lib/inventorySearch'
import { useFixtureModels, useItems, useLocations } from '@/lib/queries'

/**
 * The core table (spec §3.2 Inventory): every item with search, category and
 * location filters, and a location column. Rows open the equipment detail.
 *
 * The three filters combine (spec W-02). They are applied on the device rather
 * than by the API (issue #70): one fetch holds the whole live inventory, so
 * changing a filter is instant and goes on working in a cellar, where a list
 * narrowed by a location nobody had visited online used to be a spinner.
 *
 * The filters live in the URL rather than in component state, so a filtered
 * list can be linked to and the back button undoes a filter change. Read
 * loosely (`strict: false`) because the tests mount this component in a
 * narrower route tree than the app's.
 */
export function Inventory() {
  const navigate = useNavigate()
  const search = parseInventorySearch(useSearch({ strict: false }) as Record<string, unknown>)
  const { q = '', category = 'All', locationId = 'All', fixtureModelId } = search

  /**
   * Picking a category or a location pushes a history entry, so back undoes it.
   * Typing replaces instead — a burst of keystrokes is one step to go back
   * through rather than one per character.
   */
  const setFilters = (next: Partial<InventorySearch>, replace = false) => {
    void navigate({
      to: '.',
      search: () => parseInventorySearch({ ...search, ...next }),
      replace,
    })
  }

  const locations = useLocations()
  const fixtureModels = useFixtureModels()
  const { data: items, isPending, error } = useItems(toInventoryFilters(search))

  /**
   * The fixture model filter has no control of its own — it is arrived at from
   * a model's page (issue #30). It shows as a chip that says what is being
   * filtered and takes it off again, so the narrowed list is never a mystery.
   */
  const linkedModel =
    fixtureModelId === undefined
      ? null
      : ((fixtureModels.data ?? []).find((model) => model.id === fixtureModelId) ?? null)

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-lede">Everything the theatre owns, and where it lives.</p>
        </div>

        <div className="action-row">
          <Link to="/inventory/quick-add-lighting" className="btn btn-primary">
            Quick add lighting fixture
          </Link>
          {/* Deleted kit is kept rather than destroyed (issue #8). */}
          <Link to="/inventory/trash" className="btn btn-secondary">
            Trash
          </Link>
        </div>
      </div>

      <div className="filter-bar">
        <div className="field field-inline" style={{ flex: '1 1 260px' }}>
          <label htmlFor="search">Search</label>
          <input
            id="search"
            className="input"
            type="search"
            placeholder="Name, MADS code, brand or notes…"
            value={q}
            onChange={(event) => setFilters({ q: event.target.value }, true)}
          />
        </div>

        <div className="field field-inline" style={{ flex: '0 0 200px' }}>
          <label htmlFor="category">Category</label>
          <Select
            inputId="category"
            value={category}
            onChange={(value) =>
              setFilters({ category: value === 'All' ? undefined : (value as EquipmentCategory) })
            }
            options={[
              { value: 'All', label: 'All categories' },
              ...EQUIPMENT_CATEGORIES.map((option) => ({ value: option, label: option })),
            ]}
          />
        </div>

        <div className="field field-inline" style={{ flex: '0 0 220px' }}>
          <label htmlFor="location">Location</label>
          <Select
            inputId="location"
            value={String(locationId)}
            onChange={(value) =>
              setFilters({ locationId: value === 'All' ? undefined : Number(value) })
            }
            options={[
              { value: 'All', label: 'All locations' },
              ...(locations.data ?? []).map((location) => ({
                value: String(location.id),
                label: location.name,
              })),
            ]}
          />
        </div>
      </div>

      {fixtureModelId !== undefined && (
        <p style={{ marginTop: 0 }}>
          <span className="tag tag-accent">
            Fixture model: {linkedModel?.displayName ?? `#${fixtureModelId}`}
          </span>{' '}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setFilters({ fixtureModelId: undefined })}
          >
            Clear
          </button>
        </p>
      )}

      {error !== null ? (
        <ErrorNote error={error} />
      ) : isPending ? (
        <Loading what="Loading the inventory" />
      ) : items.length === 0 ? (
        <p className="empty-note">No equipment matches your search.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              {/*
                * MADS code and Brand had their own columns until issue #59;
                * the display name carries both now, so a column each was the
                * same text twice. The search box still looks at them —
                * searching a field that is no longer a column is exactly
                * right.
                */}
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Location</th>
                <th className="numeric">Qty</th>
                <th>Working state</th>
                <th>PAT test</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {/*
                      A record made offline opens like any other (issue #70).
                      It used to be a line of text, on the grounds that there is
                      no server id to link to — but that made a typo noticed
                      five minutes after typing it uncorrectable until the
                      device found a signal, which on a walk round a building is
                      hours. Its id is provisional and negative, `useItem` knows
                      not to ask the API for it, and an edit folds into the
                      create that has not gone yet.

                      It still says so: what is on this row has not reached the
                      inventory, and somebody has to be able to tell.
                    */}
                    <Link
                      to="/inventory/$itemId"
                      params={{ itemId: String(item.id) }}
                      className="row-link"
                    >
                      {item.displayName}
                    </Link>
                    {item.pending === true && (
                      <span className="text-muted"> · waiting to sync</span>
                    )}
                  </td>
                  <td>{item.category}</td>
                  <td className="text-muted">{item.location?.name ?? '—'}</td>
                  <td className="numeric">{item.quantity}</td>
                  <td>
                    <WorkingTag item={item} />
                  </td>
                  <td>
                    <PatTag item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
